import { Text, type TextProps } from '#app/components/Primitives/Text.js';
import { forwardRef, type KeyboardEvent, type Ref, useCallback } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { css, styled } from 'styled-components';

const StyledEditableHeadline = styled(Text)<{
  hasBottomBorder: boolean;
  hasError: boolean;
  ref: Ref<HTMLTextAreaElement>;
}>`
  ${({ hasBottomBorder, hasError, theme }) => css`
    border: 0;
    border-radius: 0;
    display: block;
    resize: none;
    width: 100%;
    margin: 0 auto;
    background-color: transparent;
    margin-top: 24px;

    &::placeholder {
      color: ${theme.colorsV2.gray50};
    }

    ${hasBottomBorder &&
    css`
      border-bottom: 1px solid ${theme.colorsV2.black};
    `}

    ${hasError &&
    css`
      border-bottom-color: ${theme.colorsV2.red50};
    `}
  `}
`;

type EditableHeadlineProps = React.ComponentPropsWithoutRef<
  typeof TextareaAutosize
> &
  TextProps & {
    readonly hasBottomBorder?: boolean;
    readonly hasError?: boolean;
  };

export const EditableHeadline = forwardRef(
  (
    {
      hasBottomBorder = false,
      hasError = false,
      onKeyDown = () => {},
      placeholder = 'Project Title',
      ...props
    }: EditableHeadlineProps,
    ref: Ref<HTMLTextAreaElement>,
  ) => {
    const handleOnKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }

        if (onKeyDown) {
          onKeyDown(event);
        }
      },
      [onKeyDown],
    );

    return (
      <StyledEditableHeadline
        as={TextareaAutosize}
        hasBottomBorder={hasBottomBorder}
        hasError={hasError}
        onKeyDown={handleOnKeyDown}
        placeholder={placeholder}
        ref={ref}
        rows={1}
        textStyle="fourthLevelHeadline"
        {...props}
      />
    );
  },
);
